import * as React from "react";
import {Component} from "react";
import {Checkbox, Field} from "bloomer";
import TextToSpeechConfig from "../../../../../components/model/TextToSpeech/TextToSpeechConfig";
import {
    AutoPlayLabel,
    InputControl,
    InputLabel,
    InputRangeValue,
    InputWarning,
    RangeInput,
    SelectField,
    SelectInputControl,
    SelectOption
} from "../../../../../components/model/TextToSpeech/TextToSpeechStyledComponents";
import {INVALID_FIELD_STYLE} from "../../../../../utils/Constants";

interface ITextToSpeechModelInputProps {
    showUserInputsOnly: boolean;
}

interface ITextToSpeechModelInputState {
    languageCode: string;
    gender: string;
    genderInvalid: boolean;
    voiceNameOptions: any;
    voiceName: string;
    voiceNameInvalid: boolean;
    speed: number;
    pitch: number;
    autoPlay: boolean;
    languageCodeOptions: any;
}

class TextToSpeechModelInput extends Component<ITextToSpeechModelInputProps, ITextToSpeechModelInputState> {
    public emptyOption: any = {
        id: "",
        name: "-"
    };
    public genderOptions: any[] = [this.emptyOption, {
        id: "male",
        name: "Male"
    }, {
        id: "female",
        name: "Female"
    }];
    public audioStyle = {width: '100%'};

    public constructor(props: ITextToSpeechModelInputProps) {
        super(props);

        this.state = {
            languageCode: "en-US",
            gender: "",
            genderInvalid: false,
            voiceNameOptions: [this.emptyOption],
            voiceName: "",
            voiceNameInvalid: false,
            speed: 1,
            pitch: 0,
            autoPlay: false,
            languageCodeOptions: []
        };
    }

    public componentDidMount() {
        this.loadConfig();
    }

    public loadConfig = async () => {
        const config = await TextToSpeechConfig.load();
        if (config) {
            this.setState({
                languageCodeOptions: Object.keys(config).map((c) => {
                    return {
                        code: c,
                        name: config[c].name
                    };
                }).sort((a: any, b: any) => {
                    return a.name > b.name ? 1 : -1;
                })
            });
        }
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            languageCode: "en-US",
            gender: "",
            genderInvalid: false,
            voiceNameOptions: [this.emptyOption],
            voiceName: "",
            voiceNameInvalid: false,
            speed: 1,
            pitch: 0,
            autoPlay: false
        });
    };

    public handleSelectLanguageChange = (event: React.FormEvent<HTMLSelectElement>) => {
        this.setState({
            languageCode: event.currentTarget.value,
            gender: "",
            genderInvalid: false,
            voiceNameOptions: [this.emptyOption],
            voiceName: "",
            voiceNameInvalid: false
        });
    };

    public handleSelectGenderChange = (event: React.FormEvent<HTMLSelectElement>) => {
        this.setState({
            gender: event.currentTarget.value,
            genderInvalid: false,
            voiceNameOptions: [this.emptyOption],
            voiceName: "",
            voiceNameInvalid: false
        }, async () => {
            if (this.state.languageCode) {
                const textToSpeechConfig = await TextToSpeechConfig.load();
                const config = textToSpeechConfig[this.state.languageCode];
                if (config && this.state.gender) {
                    const voiceNameOptions = config[this.state.gender].sort().map((item: any) => {
                        return {
                            id: item,
                            name: item
                        };
                    });
                    voiceNameOptions.unshift(this.emptyOption);
                    this.setState({
                        voiceNameOptions
                    });
                }
            }
        });
    };

    public handleSelectVoiceNameChange = (event: React.FormEvent<HTMLSelectElement>) => {
        this.setState({
            voiceName: event.currentTarget.value,
            voiceNameInvalid: false
        });
    };

    public handleSpeedChange = (event: any) => {
        this.setState({
            speed: parseFloat(event.target.value)
        });
    };

    public handlePitchChange = (event: any) => {
        this.setState({
            pitch: parseFloat(event.target.value)
        });
    };

    public handleAutoPlayChange = (event: any) => {
        this.setState({
            autoPlay: event.target.checked
        });
    };

    public validateForm = () => {
        let valid = true;

        if (!this.state.gender) {
            this.setState({
                genderInvalid: true
            });
            valid = false;
        }

        if (!this.state.voiceName) {
            this.setState({
                voiceNameInvalid: true
            });
            valid = false;
        }

        return valid;
    };

    public getFormData = (modelNamePrefix: string) => {
        const data = {
            language_code: this.state.languageCode,
            gender: this.state.gender.toUpperCase(),
            name: this.state.voiceName,
            speaking_rate: this.state.speed.toString(),
            pitch: this.state.pitch.toString(),
        };
        const formData = new FormData();
        Object.keys(data).forEach((key: string) => {
            formData.append(`${modelNamePrefix}.${key}`, data[key]);
        });
        return formData;
    };

    public getMetaData = () => {
        return {
            autoPlay: this.state.autoPlay
        };
    };

    public resetForm = () => {
        this.setState({
            languageCode: "en-US",
            gender: "",
            genderInvalid: false,
            voiceNameOptions: [this.emptyOption],
            voiceName: "",
            voiceNameInvalid: false,
            speed: 1,
            pitch: 0,
            autoPlay: false
        });
    };

    public render() {
        return <React.Fragment>
            <Field>
                <InputLabel>Language:</InputLabel>
                <SelectInputControl className="select">
                    <SelectField
                        onChange={this.handleSelectLanguageChange}
                        value={this.state.languageCode}
                    >
                        {
                            this.state.languageCodeOptions.map((lang: any) => {
                                return <SelectOption key={lang.code}
                                                     value={lang.code}>{lang.name}</SelectOption>;
                            })
                        }
                    </SelectField>
                </SelectInputControl>
            </Field>
            <Field>
                <InputLabel>Gender:</InputLabel>
                <SelectInputControl className="select">
                    <SelectField
                        style={this.state.genderInvalid ? INVALID_FIELD_STYLE : undefined}
                        onChange={this.handleSelectGenderChange}
                        value={this.state.gender}
                    >
                        {
                            this.genderOptions.map((gender) => {
                                return <SelectOption key={gender.id}
                                                     value={gender.id}>{gender.name}</SelectOption>;
                            })
                        }
                    </SelectField>
                </SelectInputControl>
                {
                    this.state.genderInvalid && <InputWarning>Please select gender.</InputWarning>
                }
            </Field>
            <Field>
                <InputLabel>Voice Name:</InputLabel>
                <SelectInputControl className="select">
                    <SelectField
                        style={this.state.voiceNameInvalid ? INVALID_FIELD_STYLE : undefined}
                        onChange={this.handleSelectVoiceNameChange}
                        value={this.state.voiceName}
                    >
                        {
                            this.state.voiceNameOptions.map((voiceName: any) => {
                                return <SelectOption key={voiceName.id}
                                                     value={voiceName.id}>{voiceName.name}</SelectOption>;
                            })
                        }
                    </SelectField>
                </SelectInputControl>
                {
                    this.state.voiceNameInvalid && <InputWarning>Please select voice name.</InputWarning>
                }
            </Field>
            <Field>
                <InputLabel>Speed:</InputLabel>
                <InputControl>
                    <RangeInput
                        type="range"
                        min="0.25"
                        max="4"
                        step="0.01"
                        onChange={this.handleSpeedChange}
                        value={this.state.speed}
                    />
                </InputControl>
                <InputRangeValue>{this.state.speed.toFixed(2)}</InputRangeValue>
            </Field>
            <Field>
                <InputLabel>Pitch:</InputLabel>
                <InputControl>
                    <RangeInput
                        type="range"
                        min="-20"
                        max="20"
                        step="0.01"
                        onChange={this.handlePitchChange}
                        value={this.state.pitch}
                    />
                </InputControl>
                <InputRangeValue>{this.state.pitch.toFixed(2)}</InputRangeValue>
            </Field>
            <Field>
                <InputLabel>Autoplay:</InputLabel>
                <InputControl>
                    <AutoPlayLabel>
                        <Checkbox
                            checked={this.state.autoPlay}
                            onChange={this.handleAutoPlayChange}
                        />&nbsp;&nbsp;{this.state.autoPlay ? 'on' : 'off'}</AutoPlayLabel>
                </InputControl>
            </Field>
        </React.Fragment>;
    }
}

export default TextToSpeechModelInput;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/CrossProvider/input/TextToSpeech/TextToSpeechModelInput.tsx